import { useRef, useState } from "react";
import messageService from "src/services/messageService";
import cloudinaryService from "src/services/cloudinaryService";

function useMessageComposer({conversation,onMessageSent,onCancelReply,replyTo}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  /* =====================================================
     EMOJI
  ===================================================== */

  const handleEmojiClick = (emojiData) => {
    setMessage((previous) => previous + emojiData.emoji);
  };

  /* =====================================================
     FILE
  ===================================================== */

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setSelectedFile(file);
    setShowAttachmentMenu(false);
  };


  const removeFile = () => {
    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };


  const getMessageType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";

    return "document";
  };

  const openFilePicker = (type) => {
    if (!fileInputRef.current) {
      return;
    }

    const acceptTypes = {
      image: "image/*",
      video: "video/*",
      audio: "audio/*",
      document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv",
    };

    fileInputRef.current.accept = acceptTypes[type] || "";
    fileInputRef.current.click();
    setShowAttachmentMenu(false);
  };

  /* =====================================================
     REPLY
  ===================================================== */

  const getReplyPreview = (reply) => {
    if (!reply) {
      return "";
    }

    const type = String(reply?.type || reply?.message_type || "").toLowerCase();
    const payload = reply?.payload;

    if (type === "text")  return payload?.body || reply?.message || "Message";
    if (type === "image") return payload?.caption ? `📷 ${payload.caption}` : "📷 Image";
    if (type === "video") return payload?.caption ? `🎥 ${payload.caption}` : "🎥 Video";
    if (type === "audio") return "🎵 Audio";
    if (type === "document") return payload?.filename ? `📄 ${payload.filename}` : "📄 Document";
    if (type === "sticker") return "😊 Sticker";
    if (type === "location") return `📍 ${payload?.name || "Location"}`;
    if (type === "reaction") return `${payload?.emoji || "👍"} Reaction`;
    if (type === "template") return `📄 ${payload?.name || "WhatsApp Template"}`;
    if (type === "interactive") return "🔘 Interactive Message";
    if (type === "contacts") {
      const contact = Array.isArray(payload) ? payload[0] : null;
      return `👤 ${contact?.name?.formatted_name || "Contact"}`;
    }

    return "Message";
  };


  const addReplyToPayload = (payload) => {
    if (!replyTo?.meta_message_id) {
      return payload;
    }

    return {
      ...payload,
      context: {
        message_id: replyTo.meta_message_id,
      },
    };
  };

  /* =====================================================
     COMMON PAYLOAD BUILDER
  ===================================================== */

  const buildMessagePayload = ({ type, payload, status = "Queued" }) => {
    return {
      direction: "Outgoing",
      type,
      payload: addReplyToPayload(payload),
      status,
    };
  };

  /* =====================================================
     COMMON SEND MESSAGE API
  ===================================================== */

  const sendMessage = async (type, payload) => {
    if (!conversation?.id) {
      return false;
    }
    const requestPayload = buildMessagePayload({ type, payload });
    const response = await messageService.create(conversation.id, requestPayload);
    // the created message resource, so the UI can append it without a refetch
    return response?.data?.data ?? true;
  };

  /* =====================================================
     TEXT MESSAGE
  ===================================================== */

  const sendTextMessage = async () => {
    const text = message.trim();

    if (!text || sending) {
      return false;
    }
    try {
      setSending(true);
      const success = await sendMessage("text", {
        preview_url: false,
        body: text,
      });

      if (success) {
        setMessage("");
        onCancelReply?.();
        onMessageSent?.(success);
      }
      return success;
    } catch (error) {
      console.error("Failed to send text message.", error);
      return false;
    } finally {
      setSending(false);
    }
  };

  /* =====================================================
     MEDIA MESSAGE
  ===================================================== */

  const sendMediaMessage = async () => {
    if (!selectedFile || !conversation?.id || sending) {
      return false;
    }
    try {
      setSending(true);
      const uploadResult = await cloudinaryService.upload(selectedFile);

      if (!uploadResult?.secure_url) {
        throw new Error("Cloudinary upload failed.");
      }

      const type = getMessageType(selectedFile);
      const link = uploadResult.secure_url;

      let payload = {
        link,
      };

      if (type === "image" || type === "video") {
        payload = {
          link,
          ...(message.trim()
            ? {
                caption: message.trim(),
              }
            : {}),
        };
      }

      if (type === "audio") {
        payload = {
          link,
        };
      }

      if (type === "document") {
        payload = {
          link,
          filename: selectedFile.name,
          ...(message.trim()
            ? {
                caption: message.trim(),
              }
            : {}),
        };
      }

      const success = await sendMessage(type, payload);

      if (success) {
        setSelectedFile(null);
        setMessage("");

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        onCancelReply?.();
        onMessageSent?.(success);
      }

      return success;
    } catch (error) {
      console.error("Failed to send media message.", error);

      return false;
    } finally {
      setSending(false);
    }
  };

  /* =====================================================
     TEMPLATE PAYLOAD BUILDER
  ===================================================== */

  const buildTemplatePayload = (selectedTemplate, values = {}) => {
    const templateComponents = [];

    selectedTemplate.components?.forEach((component) => {
      const componentType = component.type?.toUpperCase();

      if (componentType === "HEADER") {
        const headerFormat = component.format?.toUpperCase();

        if (headerFormat === "TEXT") {
          const variables = component.text?.match(/\{\{\d+\}\}/g) || [];

          if (variables.length > 0) {
            templateComponents.push({
              type: "header",
              parameters: variables.map((variable) => {
                const key = variable.replace(/[{}]/g, "");
                return {
                  type: "text",
                  text: String(values[key] || ""),
                };
              }),
            });
          }
        }

        /* HEADER IMAGE */

        if (headerFormat === "IMAGE" && values.header_image) {
          templateComponents.push({
            type: "header",
            parameters: [
              {
                type: "image",
                image: {
                  link: values.header_image,
                },
              },
            ],
          });
        }

        /* HEADER VIDEO */

        if (headerFormat === "VIDEO" && values.header_video) {
          templateComponents.push({
            type: "header",
            parameters: [
              {
                type: "video",
                video: {
                  link: values.header_video,
                },
              },
            ],
          });
        }

        /* HEADER DOCUMENT */

        if (headerFormat === "DOCUMENT" && values.header_document) {
          templateComponents.push({
            type: "header",
            parameters: [
              {
                type: "document",
                document: {
                  link: values.header_document,
                },
              },
            ],
          });
        }
      }

      /* ---------------------------------------------
           BODY
        --------------------------------------------- */

      if (componentType === "BODY") {
        const variables = component.text?.match(/\{\{\d+\}\}/g) || [];

        if (variables.length > 0) {
          templateComponents.push({
            type: "body",
            parameters: variables.map((variable) => {
              const key = variable.replace(/[{}]/g, "");

              return {
                type: "text",
                text: String(values[key] || ""),
              };
            }),
          });
        }
      }

      /* ---------------------------------------------
           BUTTONS
        --------------------------------------------- */

      if (componentType === "BUTTONS") {
        component.buttons?.forEach((button, index) => {
          const buttonType = button.type?.toUpperCase();

          if (buttonType === "URL" && button.url?.includes("{{")) {
            const variables = button.url.match(/\{\{\d+\}\}/g) || [];

            templateComponents.push({
              type: "button",
              sub_type: "url",
              index: String(index),
              parameters: variables.map((variable) => {
                const key = variable.replace(/[{}]/g, "");

                return {
                  type: "text",
                  text: String(values[key] || ""),
                };
              }),
            });
          }
        });
      }
    });

    return {
      name: selectedTemplate.name,
      language: {
        code:
          selectedTemplate.language?.code || selectedTemplate.language || "en",
      },
      components: templateComponents,
    };
  };

  /* =====================================================
     SEND TEMPLATE
  ===================================================== */

  const handleSendTemplate = async (selectedTemplate, values = {}) => {
    if (!selectedTemplate || !conversation?.id || sending) {
      return false;
    }

    try {
      setSending(true);
      const payload = buildTemplatePayload(selectedTemplate, values);
      const success = await sendMessage("template", payload);
      if (success) {
        onCancelReply?.();
        onMessageSent?.(success);
      }

      return success;
    } catch (error) {
      console.error("Failed to send template message.", error);

      return false;
    } finally {
      setSending(false);
    }
  };

  /* =====================================================
     SEND
  ===================================================== */

  const handleSend = async () => {
    if (sending || !conversation?.id) {
      return;
    }
    if (selectedFile) {
      await sendMediaMessage();
      return;
    }
    await sendTextMessage();
  };

  /* =====================================================
     KEY DOWN
  ===================================================== */

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!sending) {
        handleSend();
      }
    }
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((previous) => !previous);
    setShowAttachmentMenu(false);
  };

  const closeEmojiPicker = () => {
    setShowEmojiPicker(false);
  };

  const toggleAttachmentMenu = () => {
    setShowAttachmentMenu((previous) => !previous);
    setShowEmojiPicker(false);
  };

  const closeAttachmentMenu = () => {
    setShowAttachmentMenu(false);
  };


  const canSend = message.trim().length > 0 || !!selectedFile;

  return {
    message,
    setMessage,
    sending,
    showEmojiPicker,
    closeEmojiPicker,
    closeAttachmentMenu,
    showAttachmentMenu,
    selectedFile,
    fileInputRef,
    canSend,
    handleEmojiClick,
    handleFileChange,
    removeFile,
    openFilePicker,
    getReplyPreview,
    handleSend,
    handleKeyDown,
    handleSendTemplate,
    toggleEmojiPicker,
    toggleAttachmentMenu,
  };
}

export default useMessageComposer;
